import { useState, useEffect } from 'react';
import { parseISO, subMinutes, addMinutes, differenceInMinutes } from 'date-fns';
import { apiClient } from '@/api/client';
import { ApiPath } from '@/api/path';
import { TSMetric } from '@/query/query';
import { TEN_SECONDS } from '@/dateRanges/windowSizes';

export interface RawDataSeries {
  inlet: Array<[number, number]>;
  outlet: Array<[number, number]>;
  ambient: Array<[number, number]>;
  power: Array<[number, number]>;
  current: Array<[number, number]>;
}

export interface UseRawDataChartOptions {
  installationId: string;
  organizationId: number;
  plotStartTime: string;
  plotEndTime: string;
  showAmbient?: boolean;
  showCurrent?: boolean;
  timePaddingMinutes?: number;
  onSuccess?: (hasData: boolean) => void;
}

/**
 * Hook to fetch and process raw temperature and power data from InfluxDB
 */
export const useRawDataChart = ({
  installationId,
  organizationId,
  plotStartTime,
  plotEndTime,
  showAmbient = false,
  showCurrent = false,
  timePaddingMinutes = 30,
  onSuccess
}: UseRawDataChartOptions) => {
  const [data, setData] = useState<RawDataSeries>({
    inlet: [],
    outlet: [],
    ambient: [],
    power: [],
    current: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!plotStartTime || !plotEndTime) return;

      setLoading(true);
      setError(null);

      try {
        let startDate = parseISO(plotStartTime);
        let endDate = parseISO(plotEndTime);
        
        // Add padding to either side, unless end time is now (within 5 minutes of current time)
        const now = new Date();
        const timeDiff = Math.abs(differenceInMinutes(now, endDate));
        const isEndTimeNow = timeDiff < 5;
        
        startDate = subMinutes(startDate, timePaddingMinutes);
        if (!isEndTimeNow) {
          endDate = addMinutes(endDate, timePaddingMinutes);
        }

        // Fetch all temperature data
        const tempResponse = await apiClient.post(ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY, {
          metrics: [TSMetric.ALL_TEMPERATURES],
          window_size: TEN_SECONDS.value,
          start: startDate.toISOString(),
          stop: endDate.toISOString(),
          organization_id: organizationId,
          installation_id: [parseInt(installationId)],
          target_unit: "degF"
        });

        // Fetch power data
        const powerResponse = await apiClient.post(ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY, {
          metrics: [TSMetric.ALL_PHASE_POWERS],
          window_size: TEN_SECONDS.value,
          start: startDate.toISOString(),
          stop: endDate.toISOString(),
          organization_id: organizationId,
          installation_id: [parseInt(installationId)]
        });

        // Fetch current data if requested
        let currentResponse = null;
        if (showCurrent) {
          currentResponse = await apiClient.post(ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY, {
            metrics: [TSMetric.ALL_PHASE_CURRENTS],
            window_size: TEN_SECONDS.value,
            start: startDate.toISOString(),
            stop: endDate.toISOString(),
            organization_id: organizationId,
            installation_id: [parseInt(installationId)]
          });
        }

        // Process temperature data
        const inletPoints: Array<[number, number]> = [];
        const outletPoints: Array<[number, number]> = [];
        const ambientPoints: Array<[number, number]> = [];
        
        if (tempResponse.data && tempResponse.data.series) {
          tempResponse.data.series.forEach((series: any) => {
            const seriesName = series.name?.toLowerCase() || '';
            series.data.forEach((point: [string, number]) => {
              const timestamp = new Date(point[0]).getTime();
              const tempF = point[1]; // Already in Fahrenheit from API
              // Filter out null, undefined, NaN, and invalid values
              if (tempF != null && !isNaN(tempF) && isFinite(tempF) && timestamp && !isNaN(timestamp)) {
                if (seriesName.includes('compressor inlet')) {
                  inletPoints.push([timestamp, tempF]);
                } else if (seriesName.includes('compressor outlet')) {
                  outletPoints.push([timestamp, tempF]);
                } else if (showAmbient && (seriesName.includes('ambient') || seriesName.includes('outdoor'))) {
                  ambientPoints.push([timestamp, tempF]);
                }
              }
            });
          });
        }

        // Process power data - aggregate by timestamp (multiple phases)
        const powerMap = new Map<number, number>();
        if (powerResponse.data && powerResponse.data.series) {
          powerResponse.data.series.forEach((series: any) => {
            series.data.forEach((point: [string, number]) => {
              const timestamp = new Date(point[0]).getTime();
              const powerValue = point[1];
              if (powerValue != null && !isNaN(powerValue) && isFinite(powerValue) && timestamp && !isNaN(timestamp)) {
                const roundedTimestamp = Math.round(timestamp / 1000) * 1000;
                const existingPower = powerMap.get(roundedTimestamp) || 0;
                powerMap.set(roundedTimestamp, existingPower + powerValue);
              }
            });
          });
        }
        const powerPoints: Array<[number, number]> = Array.from(powerMap.entries())
          .map(([timestamp, power]): [number, number] => [timestamp, power])
          .sort((a, b) => a[0] - b[0]);

        // Process current data - aggregate by timestamp (multiple phases)
        const currentMap = new Map<number, number>();
        if (currentResponse && currentResponse.data && currentResponse.data.series) {
          currentResponse.data.series.forEach((series: any) => {
            series.data.forEach((point: [string, number]) => {
              const timestamp = new Date(point[0]).getTime();
              const currentValue = point[1];
              if (currentValue != null && !isNaN(currentValue) && isFinite(currentValue) && timestamp && !isNaN(timestamp)) {
                const roundedTimestamp = Math.round(timestamp / 1000) * 1000;
                const existingCurrent = currentMap.get(roundedTimestamp) || 0;
                currentMap.set(roundedTimestamp, existingCurrent + currentValue);
              }
            });
          });
        }
        const currentPoints: Array<[number, number]> = Array.from(currentMap.entries())
          .map(([timestamp, current]): [number, number] => [timestamp, current])
          .sort((a, b) => a[0] - b[0]);

        // Sort all data by timestamp to prevent connecting lines across gaps
        inletPoints.sort((a, b) => a[0] - b[0]);
        outletPoints.sort((a, b) => a[0] - b[0]);
        ambientPoints.sort((a, b) => a[0] - b[0]);
        powerPoints.sort((a, b) => a[0] - b[0]);
        currentPoints.sort((a, b) => a[0] - b[0]);

        const newData: RawDataSeries = {
          inlet: inletPoints,
          outlet: outletPoints,
          ambient: ambientPoints,
          power: powerPoints,
          current: currentPoints
        };

        setData(newData);
        
        const hasData = inletPoints.length > 0 || outletPoints.length > 0 || ambientPoints.length > 0 || powerPoints.length > 0 || currentPoints.length > 0;
        if (onSuccess) {
          onSuccess(hasData);
        }
      } catch (err) {
        console.warn('Failed to fetch raw data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        setData({ inlet: [], outlet: [], ambient: [], power: [], current: [] });
        if (onSuccess) {
          onSuccess(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [installationId, organizationId, plotStartTime, plotEndTime, showAmbient, showCurrent, timePaddingMinutes, refreshKey]);

  return { data, loading, error, refresh };
};

